import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: "start" | "end";
  position?: "top" | "bottom";
  className?: string;
}

interface DropdownMenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  className?: string;
}

interface DropdownMenuSeparatorProps {
  className?: string;
}

interface DropdownMenuLabelProps {
  children: React.ReactNode;
  className?: string;
}

function DropdownMenuRoot({
  trigger,
  children,
  align = "end",
  position = "bottom",
  className,
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
    return undefined;
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        {trigger}
      </div>

      {isOpen && (
        <div
          className={cn(
            "absolute w-56 rounded-lg border border-border-light bg-background-secondary shadow-lg z-50",
            position === "top" ? "bottom-full mb-2" : "top-full mt-2",
            align === "end" ? "right-0" : "left-0",
          )}
          role="menu"
        >
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownMenuItem({
  children,
  onClick,
  disabled = false,
  destructive = false,
  className,
}: DropdownMenuItemProps) {
  return (
    <button
      className={cn(
        "flex w-full items-center px-3 py-2 text-sm text-left transition-colors",
        "hover:bg-background-tertiary focus:bg-background-tertiary focus:outline-none",
        "disabled:cursor-not-allowed",
        destructive && "text-error hover:bg-error-light focus:bg-error-light",
        className,
      )}
      onClick={onClick}
      disabled={disabled}
      role="menuitem"
    >
      {children}
    </button>
  );
}

function DropdownMenuSeparator({ className }: DropdownMenuSeparatorProps) {
  return (
    <hr
      className={cn("my-1 border-border-light", className)}
      role="separator"
    />
  );
}

function DropdownMenuLabel({ children, className }: DropdownMenuLabelProps) {
  return (
    <div className={cn("p-3 border-b border-border-light", className)}>
      {children}
    </div>
  );
}

export {
  DropdownMenuRoot as DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
};
